// =========================================================================
// Progreso · antropometría · persistencia
//
// Capa fina y aparte de la lógica, para que `antropometria.ts` se pueda
// probar sin navegador. Sigue la convención de claves de DataService
// (`helpydoc_data_<recurso>`).
// =========================================================================
import { normalizar } from './antropometria';
import type { MedidaCorporal } from './tipos';

import {
  REGISTRO_VACIO,
  confirmar,
  guardarBorrador,
  marcarSincronizado,
  type DatosDeclarados,
  type RegistroInicial,
} from './registro-inicial';

const CLAVE = 'helpydoc_data_antropometria';
const CLAVE_REGISTRO = 'helpydoc_data_registro_inicial';

/** Todas las medidas guardadas, normalizadas (una por día, en orden). */
export function leerMedidas(): MedidaCorporal[] {
  try {
    const bruto = localStorage.getItem(CLAVE);
    if (!bruto) return [];
    const datos = JSON.parse(bruto) as unknown;
    if (!Array.isArray(datos)) return [];
    return normalizar(datos as MedidaCorporal[]);
  } catch {
    // Un almacén ilegible se trata como vacío: nunca se tira la pantalla
    // abajo por no poder leer un histórico.
    return [];
  }
}

/**
 * Registra una medida. `hoy` se inyecta para que las pruebas no dependan del
 * reloj. Guardar solo peso o solo cintura es válido: se fusiona con lo que ya
 * hubiera ese día en lugar de pisarlo.
 */
export function registrarMedida(
  medida: Omit<MedidaCorporal, 'fecha'> & { fecha?: string },
  hoy: string,
): MedidaCorporal[] {
  const actualizadas = normalizar([
    ...leerMedidas(),
    { ...medida, fecha: (medida.fecha ?? hoy).slice(0, 10) },
  ]);
  try {
    localStorage.setItem(CLAVE, JSON.stringify(actualizadas));
  } catch (err) {
    console.warn('[progreso] no se pudo guardar la medida:', err);
  }
  return actualizadas;
}

// ── Registro inicial ──────────────────────────────────────────────────────

export function leerRegistroInicial(): RegistroInicial {
  try {
    const bruto = localStorage.getItem(CLAVE_REGISTRO);
    if (!bruto) return REGISTRO_VACIO;
    const r = JSON.parse(bruto) as RegistroInicial;
    // Un registro ilegible se trata como vacío, PERO nunca como no sellado si
    // la marca está ahí: perder el sello por un parseo raro abriría justo lo
    // que el sello cierra.
    return { ...REGISTRO_VACIO, ...r, correcciones: r.correcciones ?? [] };
  } catch {
    return REGISTRO_VACIO;
  }
}

function escribirRegistro(r: RegistroInicial): RegistroInicial {
  try {
    localStorage.setItem(CLAVE_REGISTRO, JSON.stringify(r));
  } catch (err) {
    console.warn('[progreso] no se pudo guardar el registro inicial:', err);
  }
  return r;
}

/** Guarda mientras se rellena. No hace nada si ya está sellado. */
export function guardarBorradorRegistro(datos: DatosDeclarados): RegistroInicial {
  return escribirRegistro(guardarBorrador(leerRegistroInicial(), datos));
}

/** Cierra el registro. A partir de aquí solo lo toca un administrador. */
export function confirmarRegistro(datos: DatosDeclarados, ahora: string): RegistroInicial {
  return escribirRegistro(confirmar(leerRegistroInicial(), datos, ahora));
}

/** Persiste un registro ya corregido por `corregir()`. */
export function guardarRegistroCorregido(r: RegistroInicial): RegistroInicial {
  return escribirRegistro(r);
}

/** Marca que el servidor ya confirmó la valoración inicial. No hace nada si aún no está sellada en local. */
export function marcarRegistroSincronizado(ahora: string): RegistroInicial {
  return escribirRegistro(marcarSincronizado(leerRegistroInicial(), ahora));
}

/** Borra el histórico. Existe para las pruebas y para el borrado de datos. */
export function limpiarMedidas(): void {
  try {
    localStorage.removeItem(CLAVE);
    localStorage.removeItem(CLAVE_REGISTRO);
  } catch {
    /* sin almacén no hay nada que limpiar */
  }
}
